import * as Form from "@radix-ui/react-form";

import {
  clearAuthError,
  login,
  selectAuthError,
  selectIsAuth,
  selectRequires2FA,
} from "@/features/auth/authSlice";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  LOGIN_INACTIVITY_NOTICE,
  LOGIN_PASSWORD_LABEL,
  LOGIN_SUBMIT,
  LOGIN_TITLE,
  LOGIN_USERNAME_LABEL,
} from "@/constants/ui";
import { FormField } from "./form-field";
import Logo from "./Logo";
import { useNavigate } from "react-router-dom";

// NOTE: disabling routerId input until after demo, retrieving it from env vars instead

/**
 * Login component renders the login form and handles the authentication process.
 *
 * @component
 * @returns {React.ReactElement} The login page.
 */
const Login = (): React.ReactElement => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // const [routerIdLocal, setRouterIdLocal] = useState<string | number>("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const authError = useAppSelector(selectAuthError) as string | null;
  const isAuth = useAppSelector(selectIsAuth);
  const requires2FA = useAppSelector(selectRequires2FA);
  const [timedOut] = useState(() => {
    const reason = sessionStorage.getItem("loggedOutReason");
    if (reason === "inactivity") {
      sessionStorage.removeItem("loggedOutReason");
      return true;
    }
    return false;
  });

  // Redirect to /chat if authenticated
  useEffect(() => {
    if (isAuth) {
      navigate("/chat", { replace: true });
    }
  }, [isAuth, navigate]);

  // Redirect to /2fa if 2FA is required
  useEffect(() => {
    if (requires2FA) {
      navigate("/2fa", { replace: true });
    }
  }, [requires2FA, navigate]);

  /**
   * Handles the login form submission.
   *
   * @param {React.FormEvent<HTMLFormElement>} event - The form submission event.
   */
  const handleLogin: React.FormEventHandler<HTMLFormElement> = async (
    event
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const loginParams = {
      routerId: Number(formData.get("routerId")), // may need to read env VITE_ROUTER_ID, may be removed for now
      // Trimmed: phone autocorrect can append a space, which the router's lookup ignores but we wouldn't.
      username: (formData.get("username") as string).trim(),
      password: formData.get("password") as string,
    };

    await dispatch(login(loginParams));
  };

  return (
    <div className="flex items-center justify-center h-dvh bg-neutral-100 text-neutral-600">
      <div className="w-full max-w-sm p-4 bg-white border rounded border-neutral-300">
        <div className="flex flex-col gap-2">
          <Logo />
          <h2 className="text-2xl font-bold">{LOGIN_TITLE}</h2>
          {timedOut && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              {LOGIN_INACTIVITY_NOTICE}
            </p>
          )}
          {authError && <p className="text-lg text-red-600">{authError}</p>}
        </div>

        <Form.Root onSubmit={handleLogin} className="mt-3">
          {/* <FormField
            name="routerId"
            label="Router ID"
            value={routerIdLocal}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = Number(e.target.value);
              setRouterIdLocal(value > 0 ? value : "");
              dispatch(clearAuthError());
            }}
            error={authError}
          /> */}
          <FormField
            name="username"
            label={LOGIN_USERNAME_LABEL}
            // Phone keyboards capitalize the first letter by default.
            inputProps={{ autoCapitalize: "none", autoCorrect: "off", spellCheck: false, autoComplete: "username" }}
            value={username}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setUsername(e.target.value);
              dispatch(clearAuthError());
            }}
            error={authError}
          />
          <FormField
            name="password"
            label={LOGIN_PASSWORD_LABEL}
            type="password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setPassword(e.target.value);
              dispatch(clearAuthError());
            }}
            error={authError}
          />

          <Form.Submit asChild>
            <Button type="submit" className="mt-3">
              {LOGIN_SUBMIT}
            </Button>
          </Form.Submit>
        </Form.Root>
      </div>
    </div>
  );
};

export default Login;
